import { EdgeParams, ReactAppProcessEnv, StandardProceessEnv, isEdgeParams } from '@iotv/iotv-v3-types'
import { v4 } from 'uuid'
import { DEBUG_OVERRIDE, NOT_LAMBDA } from '../../lambda'

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)
const { REACT_APP_DEPLOYMENT_STAGE, stage, NODE_ENV } = process.env as ReactAppProcessEnv & StandardProceessEnv

const throwOnDuplicateNodeIds = ['dev', 'test'].includes(stage) || ['dev', 'test'].includes(REACT_APP_DEPLOYMENT_STAGE) || NODE_ENV === 'development'

type BranchingQueryDefinitionAttributes = {
  /** the query path to evaluate before sub path definitions */
  path: EdgeParams[]
  /** an array of subqueries to evaluate using the result items of this path */
  subQueries: IBranchingQueryDefinition[]
  /** a unique id is mandatory to allow recursive structures to be dissembled and rehyrated ( eg when sending from UI to cloud systems via api ) */
  id: string
}

const isBranchingQueryDefinitionAttributes = (x: any): x is BranchingQueryDefinitionAttributes => {
  const y = x as BranchingQueryDefinitionAttributes
  return (
    typeof y.id === 'string' && y.subQueries?.every((subQuery) => isBranchingQueryDefinitionAttributes(subQuery) && y.path?.every((path) => isEdgeParams(path)))
  )
}

export type DehyratedBranchingQueryDefinition = Omit<BranchingQueryDefinitionAttributes, 'subQueries'> & { subQueryIds: string[]; isApexQuery?: boolean }

export const isDehyratedBranchingQueryDefinition = (x: any): x is DehyratedBranchingQueryDefinition => {
  const y = x as DehyratedBranchingQueryDefinition
  return typeof y.id === 'string' && y.subQueryIds?.every((subQueryId) => typeof subQueryId === 'string' && y.path?.every((path) => isEdgeParams(path)))
}

export const isDehyratedBranchingQueryDefinitionArray = (x: any): x is DehyratedBranchingQueryDefinition[] => {
  let isValid = false
  if (x instanceof Array) {
    isValid = x.every((queryDef) => isDehyratedBranchingQueryDefinition(queryDef))
  }
  return isValid
}

type DefinitionKeyMap = { [id: string]: BranchingQueryDefinition }

function getZombieIndex(this: DehyratedBranchingQueryDefinition[], id: string) {
  return this.findIndex((other) => other.id === id)
}

function pluckApexQuery(this: DehyratedBranchingQueryDefinition[]) {
  return this.find((other) => other.isApexQuery === true)
}

export interface IBranchingQueryDefinition extends Required<BranchingQueryDefinitionAttributes> {
  path: EdgeParams[]
  subQueries: IBranchingQueryDefinition[]
  isApexQuery?: boolean

  addSubQuery: (subQuery: IBranchingQueryDefinition) => void
  dehydrate: (isApexQuery: boolean, dehydratedIds: string[]) => DehyratedBranchingQueryDefinition[]

  /**
   * zombieQueryDefinitions holds the dehydrated definition and is consumed as tree is instantiated
   * queryMap holds previously instantiated subqueries by id so that they can be reference where recursion occurs
   * */
  hydrateFromList: (zombieQueryDefinitions: DehyratedBranchingQueryDefinition[], queryMap: DefinitionKeyMap, iteration: number) => void

  getSubQueryById: ({ id, visitedIds }: GetSubQueryByIdInput) => BranchingQueryDefinition | null

  summarize: (visitedIds?: string[]) => Record<string, any>
}

/* returns an object representing a query that can be evaluated recurively providing a concise defintion to parse a query path through data */
export class BranchingQueryDefinition implements IBranchingQueryDefinition {
  static rehydrate = (zombieQueryDefinitions: DehyratedBranchingQueryDefinition[]) => {
    const queryMap: DefinitionKeyMap = {}
    let apexQuery: BranchingQueryDefinition | undefined = undefined
    const apexQueryZombie = pluckApexQuery.apply(zombieQueryDefinitions)
    if (apexQueryZombie) {
      apexQuery = new BranchingQueryDefinition({ ...apexQueryZombie, subQueries: [] })
      apexQuery.hydrateFromList(zombieQueryDefinitions, queryMap)
    }
    return apexQuery
  }

  constructor({ path, subQueries = [], id }: BranchingQueryDefinitionAttributes) {
    this.path = path
    this.subQueries = subQueries
    this.id = id ?? v4()
  }

  id: string
  path: EdgeParams[]
  subQueries: IBranchingQueryDefinition[]

  addSubQuery = (subQuery: IBranchingQueryDefinition) => {
    this.subQueries.push(subQuery)
  }

  addSubQueries = (subQueries: IBranchingQueryDefinition[]) => {
    this.subQueries.push(...subQueries)
  }

  dehydrate = (isApexQuery: boolean = true, dehydratedIds: string[] = []) => {
    const thisDehydrated: DehyratedBranchingQueryDefinition = {
      path: this.path,
      id: this.id,
      subQueryIds: this.subQueries.map((subQuery) => subQuery.id),
    }
    if (isApexQuery) {
      thisDehydrated.isApexQuery = true
    }

    if (throwOnDuplicateNodeIds && dehydratedIds.includes(this.id)) {
      throw new Error(`query part ${this.id} is already named in dehydratedNodeIds: ${dehydratedIds.join(', ')}`)
    }
    dehydratedIds.push(this.id)

    const dehydrated = this.subQueries.reduce(
      (a, c) => {
        if (!dehydratedIds.includes(c.id)) {
          a.push(...c.dehydrate(false, dehydratedIds))
        }
        return a
      },
      [thisDehydrated],
    )

    return dehydrated
  }

  hydrateFromList = (zombieQueryDefinitions: DehyratedBranchingQueryDefinition[], queryMap: DefinitionKeyMap) => {
    const parentId = this.id
    queryMap[parentId] = this
    const thisEntryIdx = getZombieIndex.apply(zombieQueryDefinitions, [this.id])
    const thisEntry = zombieQueryDefinitions[thisEntryIdx]
    const removedEntry = zombieQueryDefinitions.splice(thisEntryIdx, 1)
    if (thisEntry) {
      const subQueriesIds = thisEntry.subQueryIds
      for (let subQueryId of subQueriesIds) {
        let resolvedSubQuery: BranchingQueryDefinition | undefined
        /** in the case of recursive structures get the previously processed node from the map */
        if (queryMap[subQueryId]) {
          resolvedSubQuery = queryMap[subQueryId]
          this.addSubQuery(resolvedSubQuery)
          /** where not previsouly processed get new query and add to map */
        } else {
          const idx = getZombieIndex.apply(zombieQueryDefinitions, [subQueryId])
          resolvedSubQuery = new BranchingQueryDefinition({ ...zombieQueryDefinitions[idx], subQueries: [] })
          queryMap[resolvedSubQuery.id] = resolvedSubQuery
          this.addSubQuery(resolvedSubQuery)
          resolvedSubQuery.hydrateFromList(zombieQueryDefinitions, queryMap)
        }
      }
    }
  }

  /** returns matched subquery if exists. The purpose of this is to be able to manipulate automatically generated query definitions, where the generator uses a predicatble id for each sub query
   * @param visitedIds is a recursion limiting array which does not need to be provided by caller
   */
  getSubQueryById = ({ id, visitedIds = [] }: GetSubQueryByIdInput): BranchingQueryDefinition | null => {
    let res: BranchingQueryDefinition | null = null
    visitedIds.push(this.id)
    if (this.id === id) {
      res = this
    } else {
      for (let subQuery of this.subQueries) {
        if (!visitedIds.includes(subQuery.id)) {
          const getSubQueryByIdFromSubQueryRes = subQuery.getSubQueryById({ id, visitedIds })
          if (getSubQueryByIdFromSubQueryRes) {
            res = getSubQueryByIdFromSubQueryRes
            break
          }
        }
      }
    }
    return res
  }

  /** adds subQuery to matched nodeId.  The purpose of this is to be able to manipulate automatically generated query definitions, where the generator uses a predicatble id for each sub query
   * The exact id may not be predictable where the query definition is manufactured by a function which assigns id prefixes to avoid duplicate ids in composite queries such as
   * getNestedStructureBranchingQuery
   *
   */
  addSubQueryById = (nodeId: string, additionalQuery: BranchingQueryDefinition) => {
    const matchedQuery = this.getSubQueryById({ id: nodeId })
    if (matchedQuery) {
      matchedQuery.addSubQuery(additionalQuery)
      debug && console.log(`attached ${additionalQuery.id} extension to ${nodeId}`)
    } else {
      console.log(`Did not attache ${additionalQuery.id} extension to ${nodeId}!!`)
    }
  }

  summarize = (visitedIds: string[] = []) => {
    return {
      id: this.id,
      path: this.path,
      subQueries: this.subQueries
        .filter((subQuery) => !visitedIds.includes(subQuery.id))
        .map((subQuery) => {
          visitedIds.push(subQuery.id)
          return subQuery.summarize(visitedIds)
        }),
    }
  }
}

type GetSubQueryByIdInput = { id: string; visitedIds?: string[] }
